import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  InputBase,
  Pagination,
  TableContainer,
  Paper
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import CustomText, { textStyles } from "../../../../components/CustomText";
import emptypayment from "../../../../assets/emptypayment.png";
import TruncatedCell from "../../../../components/TruncatedCell";
/* ---------------- styles ---------------- */
const headerStyle = {
  color: "#44546F",
  fontSize: "15px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
    whiteSpace: 'nowrap', // ✅ Prevent text wrap
   
      overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const dataStyle = {
  color: "#172B4D",
  fontSize: "13px",
  fontWeight: 400,
  fontFamily: "Poppins, sans-serif",
   whiteSpace: 'nowrap', // ✅ Prevent text wrap
   
      overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const formatDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  return isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const ROWS_PER_PAGE = 5;

/* ================= COMPONENT ================= */
const WorkerAbsence = ({ workerId }) => {
  const { t } = useTranslation();
  const token = useSelector((s) => s.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [allRows, setAllRows] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  /* ------------ FETCH DATA (ONCE) ------------ */
  const fetchAbsences = async () => {
    if (!workerId || !decryptedToken) return;

    try {
      setInitialLoading(true);

      const url = `${BASE_URL}/absences/admin/absences?worker_id=${workerId}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const json = await res.json();

      if (!json.error && json.data?.absences) {
        const mapped = json.data.absences.map((item) => ({
          id: item.id,
          workerName: item.worker?.name || "-",
          department: item.worker?.department || "-",
          type: item.absence?.type?.name || "-",
          paid: item.absence?.type?.isPaid,
          startDate: formatDate(item.absence?.startDate),
          endDate: formatDate(item.absence?.endDate),
          partial: item.absence?.isPartial,
          comment: item.absence?.comment || "-",
        }));

        setAllRows(mapped);
      } else {
        setAllRows([]);
      }
    } catch (e) {
      console.error("Absence fetch error", e);
      setAllRows([]);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setSearchTerm("");
    fetchAbsences();
    // eslint-disable-next-line
  }, [workerId]);

  /* ------------ SEARCH (CLIENT SIDE) ------------ */
  const filteredRows = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return allRows;

    return allRows.filter((r) =>
      [
        r.workerName,
        r.department,
        r.type,
        r.comment,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [allRows, searchTerm]);

  /* ------------ PAGINATION (CLIENT SIDE) ------------ */
  const totalPages = Math.ceil(filteredRows.length / ROWS_PER_PAGE);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filteredRows.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRows, page]);

  /* ================= UI ================= */
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        border: "2px solid #091E4224",
        py: 2,
        mx: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          pb: 2,
          borderBottom: "2px solid #091E4224",
          alignItems: "center",
        }}
      >
        <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 600,
            fontSize: 18,
            color: "#003149",
          }}
        >
          {t("absences")}
        </CustomText>

        <Box
          sx={{
            px: 1,
            border: "2px solid #091E4224",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            minWidth: 250,
          }}
        >
          <InputBase
            placeholder={t("search")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            sx={{ fontSize: 14, width: "100%" }}
          />
          <SearchIcon sx={{ color: "#888" }} />
        </Box>
      </Box>

      {initialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
         <TableContainer component={Paper} sx={{ boxShadow: "none", width: "100%", overflowX: "auto" }}>
              <Table sx={{ minWidth: "700px" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle}>ID</TableCell>
                <TableCell sx={headerStyle}>{t("employee")}</TableCell>
                <TableCell sx={headerStyle}>{t("department")}</TableCell>
                <TableCell sx={headerStyle}>{t("type")}</TableCell>
                <TableCell sx={headerStyle}>{t("paid")}</TableCell>
                <TableCell sx={headerStyle}>{t("startDate")}</TableCell>
                <TableCell sx={headerStyle}>{t("endDate")}</TableCell>
                <TableCell sx={headerStyle}>{t("partial")}</TableCell>
                <TableCell sx={headerStyle}>{t("comments")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.length ? (
                paginatedRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={dataStyle}>{r.id}</TableCell>
                    <TableCell sx={dataStyle}>{r.workerName}</TableCell>
                    <TableCell sx={dataStyle}>{r.department}</TableCell>
                    <TableCell sx={dataStyle}>{r.type}</TableCell>
                    <TableCell sx={dataStyle}>
                      {r.paid ? t("paid") : t("unpaid")}
                    </TableCell>
                    <TableCell sx={dataStyle}>{r.startDate}</TableCell>
                    <TableCell sx={dataStyle}>{r.endDate}</TableCell>
                    <TableCell sx={dataStyle}>
                      {r.partial ? t("yes") : t("no")}
                    </TableCell>
                    <TableCell sx={dataStyle} title={r.comment}>
                      {/* {r.comment} */}
                      <TruncatedCell text={r.comment}/>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <img src={emptypayment} width={180} alt="empty" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
             <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
                     shape="rounded"
          color="primary"
          size="small"
                sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                },
                "& .Mui-selected": {
                  backgroundColor: "#E9F3FF",
                  color: "#006EC2",
                },
              }}
            />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default WorkerAbsence;
